import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useMemo, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { Box, IconButton, Switch, Typography } from '@mui/material';
import { SecondaryGrid } from '../../../../components/common/grid/SecondaryGrid';
import { TableHeader } from '../../../../components/common/header/tableHeader/TableHeader';
import { Trash, DocumentText1, TaskSquare, Edit } from 'iconsax-react';
import { ProjectTaskDialog } from '../dialog/ProjectTaskDialog';
import { DeleteConfirmationDialog } from '../dialog/DeleteConfirmationDialog';
import { GetAllAssignmentReasonAsync, CreateAssignmentReasonAsync, UpdateAssignmentReasonAsync, UpdateAssignmentReasonStatusAsync, DeleteAssignmentReasonAsync } from '@application/features/assignmentReasons/assignmentReasonThunk';
import { ContractType } from '@application/common/enums/contract-type';
import { addErrorNotification } from '@application/features/common/notificationSlice';
import { AssignmentReasonStatus } from '@application/features/assignmentReasons/models/assignmentReasonsDto';
export const ProjectTaskModule = ({ openAddOnMount, onAddDialogClosed }) => {
    const dispatch = useAppDispatch();
    const assignmentReasons = useAppSelector((state) => state.assignmentReason.AssignmentReasons);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState('add');
    const [selectedItem, setSelectedItem] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [idToDelete, setIdToDelete] = useState(null);
    const rows = useMemo(() => {
        if (!assignmentReasons || assignmentReasons.length === 0)
            return [];
        const sortedReasons = [...assignmentReasons].sort((a, b) => {
            const timeA = a.createdDate ? new Date(a.createdDate).getTime() : 0;
            const timeB = b.createdDate ? new Date(b.createdDate).getTime() : 0;
            if (timeA !== timeB) {
                return timeB - timeA;
            }
            // Fallback for older records where createdDate may be missing/equal.
            return (b.order ?? 0) - (a.order ?? 0);
        });
        return sortedReasons.map((r) => {
            const ct = r.contractType?.trim();
            const type = ct === ContractType.Seconded ? ContractType.Seconded : ct === ContractType.Freelancer ? ContractType.Freelancer : null;
            return { id: r.id, name: r.name, reasonValue: r.reasonValue, type, status: r.status ?? AssignmentReasonStatus.Enabled };
        });
    }, [assignmentReasons]);
    useEffect(() => {
        dispatch(GetAllAssignmentReasonAsync());
    }, [dispatch]);
    useEffect(() => {
        if (openAddOnMount) {
            setSelectedItem(null);
            setDialogMode('add');
            setDialogOpen(true);
            onAddDialogClosed?.();
        }
    }, [openAddOnMount]);
    const openAdd = () => {
        setSelectedItem(null);
        setDialogMode('add');
        setDialogOpen(true);
    };
    const openEdit = (item) => {
        setSelectedItem(item);
        setDialogMode('edit');
        setDialogOpen(true);
    };
    const closeDialog = () => {
        setDialogOpen(false);
        setSelectedItem(null);
    };
    const onSave = (item) => {
        const nameTrimmed = item.name?.trim() ?? '';
        const itemType = item.type ?? null;
        const isDuplicate = rows.some((r) => (r.name?.trim() ?? '') === nameTrimmed &&
            (r.type ?? null) === itemType &&
            (dialogMode !== 'edit' || r.id !== selectedItem?.id));
        if (isDuplicate) {
            dispatch(addErrorNotification('Project Task with same name already exists'));
            return;
        }
        if (dialogMode === 'edit' && selectedItem) {
            dispatch(UpdateAssignmentReasonAsync({
                id: selectedItem.id,
                reasonName: item.name,
                reasonValue: item.reasonValue,
                contractType: item.type ?? undefined
            }))
                .unwrap()
                .then(closeDialog)
                .catch(() => {
                /* Error toast shown by global notificationSlice isRejected matcher (400/404) */
            });
            return;
        }
        dispatch(CreateAssignmentReasonAsync({
            reasonName: item.name,
            reasonValue: item.reasonValue,
            contractType: item.type ?? undefined
        }))
            .unwrap()
            .then(() => {
            closeDialog();
            dispatch(GetAllAssignmentReasonAsync());
        })
            .catch((err) => {
            dispatch(addErrorNotification(err?.error ?? 'Failed to create project task.'));
        });
    };
    // Display only: name the project task in the delete confirmation.
    const taskToDeleteName = rows.find((row) => row.id === idToDelete)?.name;
    const openDelete = (id) => {
        setIdToDelete(id);
        setDeleteDialogOpen(true);
    };
    const confirmDelete = () => {
        if (!idToDelete)
            return;
        dispatch(DeleteAssignmentReasonAsync(idToDelete))
            .unwrap()
            .then(() => {
            setDeleteDialogOpen(false);
            setIdToDelete(null);
            dispatch(GetAllAssignmentReasonAsync());
        })
            .catch((err) => {
            // dispatch(addErrorNotification(err?.error ?? 'Failed to delete project task.'));
        });
    };
    const onStatusToggle = (row, checked) => {
        const newStatus = checked ? AssignmentReasonStatus.Enabled : AssignmentReasonStatus.Disabled;
        dispatch(UpdateAssignmentReasonStatusAsync({
            id: row.id,
            status: newStatus
        }))
            .unwrap()
            .catch(() => {
            /* Error toast shown by global notificationSlice isRejected matcher (400/404) */
        });
    };
    const columns = [
        {
            field: 'name',
            headerName: 'Project Task Name',
            flex: 1,
            minWidth: 200,
            renderHeader: () => _jsx(TableHeader, { icon: TaskSquare, text: 'Project Task Name' })
        },
        {
            field: 'reasonValue',
            headerName: 'Project Task Value',
            flex: 1,
            minWidth: 200,
            renderHeader: () => _jsx(TableHeader, { icon: DocumentText1, text: 'Project Task Value' }),
            renderCell: (params) => _jsx(Typography, { variant: 'body2', children: params.row.reasonValue || '—' })
        },
        {
            field: 'type',
            headerName: 'Type',
            flex: 0.8,
            minWidth: 140,
            renderHeader: () => _jsx(TableHeader, { icon: DocumentText1, text: 'Type' }),
            renderCell: (params) => (_jsx(Typography, { variant: 'body2', children: params.row.type === ContractType.Seconded
                    ? 'Seconded'
                    : params.row.type === ContractType.Freelancer
                        ? 'Freelancer'
                        : params.row.name === 'Workday'
                            ? 'Seconded/Freelancer'
                            : '—' }))
        },
        {
            field: 'actions',
            headerName: 'Actions',
            headerAlign: 'center',
            align: 'center',
            flex: 0.8,
            minWidth: 150,
            renderHeader: () => _jsx(TableHeader, { icon: DocumentText1, text: 'Actions' }),
            renderCell: (params) => params.row.status === AssignmentReasonStatus.Locked ? (_jsx(Typography, { variant: 'body2', color: 'text.secondary', children: "\u2014" })) : (_jsxs(Box, { sx: { display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center' }, children: [_jsx(Switch, { checked: params.row.status === AssignmentReasonStatus.Enabled, onChange: (_, checked) => onStatusToggle(params.row, checked), size: 'small', inputProps: { 'aria-label': `${params.row.name} enabled` } }), _jsx(IconButton, { onClick: () => openEdit(params.row), size: 'small', "aria-label": `Edit project task ${params.row.name}`, children: _jsx(Edit, {}) }), _jsx(IconButton, { onClick: () => openDelete(params.row.id), sx: { color: '#FF0000' }, size: 'small', "aria-label": `Delete project task ${params.row.name}`, children: _jsx(Trash, {}) })] }))
        }
    ];
    return (_jsxs(_Fragment, { children: [_jsx(SecondaryGrid, { columns: columns, rows: rows }), _jsx(ProjectTaskDialog, { open: dialogOpen, handleClose: closeDialog, type: dialogMode, item: selectedItem, onSave: onSave }, `project-task-${selectedItem?.id ?? 'add'}`), _jsx(DeleteConfirmationDialog, { open: deleteDialogOpen, handleClose: () => {
                    setDeleteDialogOpen(false);
                    setIdToDelete(null);
                }, onConfirm: confirmDelete, title: 'Delete project task', subTitle: taskToDeleteName
                    ? `"${taskToDeleteName}" will be permanently deleted. This action cannot be undone.`
                    : 'Are you sure you want to delete this item? This action cannot be undone.' })] }));
};
